export interface InstalledBasePathNode {
  id?: string;
  title?: string | null;
  nodeType?: { key?: string | null } | null;
}

export interface InstalledBaseNodeWithPath {
  id?: string;
  nodeType?: { key?: string | null } | null;
  pathNodes?: (InstalledBasePathNode | null)[] | null;
}

// `pathNodes` is ordered root → leaf. The schema documents it as parents only,
// but some responses include the node itself as the last element — drop it so
// callers always get ancestors only.
export const getPathNodesAbove = (
  node?: InstalledBaseNodeWithPath | null
): InstalledBasePathNode[] => {
  if (!node) return [];
  const pathNodes = (node.pathNodes ?? []).filter(Boolean) as InstalledBasePathNode[];
  if (pathNodes.length && node.id && pathNodes[pathNodes.length - 1].id === node.id) {
    return pathNodes.slice(0, -1);
  }
  return pathNodes;
};

export const getParentPathNode = (
  node?: InstalledBaseNodeWithPath | null
): InstalledBasePathNode | null => {
  const above = getPathNodesAbove(node);
  return above.length ? above[above.length - 1] : null;
};

export const buildInstalledBasePathString = (
  node?: InstalledBaseNodeWithPath | null
): string =>
  getPathNodesAbove(node)
    .map((n) => n.title ?? "")
    .filter((title) => title !== "")
    .join(" / ");

// "In the trash" is pure tree containment (IBM-586): the node is the TRASH
// node itself, or a TRASH node appears among its ancestors. Requires the
// caller's pathNodes selection to include nodeType.key.
export const isNodeInTrash = (
  node?: InstalledBaseNodeWithPath | null
): boolean => {
  if (!node) return false;
  if (node.nodeType?.key === "TRASH") return true;
  return (node.pathNodes ?? []).some((n) => n?.nodeType?.key === "TRASH");
};

// A User Pool is the tree of a self-registered user without a client: rooted at a
// USER_POOL node, it only holds (sub-)assets. Same containment rule and the same
// pathNodes requirement as isNodeInTrash.
export const isNodeInUserPool = (
  node?: InstalledBaseNodeWithPath | null
): boolean => {
  if (!node) return false;
  if (node.nodeType?.key === "USER_POOL") return true;
  return (node.pathNodes ?? []).some((n) => n?.nodeType?.key === "USER_POOL");
};
